import {route} from './routes';
import React, {useEffect} from 'react';
import useState from 'react-usestateref';
import {useTranslation} from 'react-i18next';
import {Routes, Route, useNavigate} from 'react-router-dom';
import {
    useAuthentication,
    SessionStorage,
    useErrors,
    isJsonValid,
    Response,
    NotFound,
    Container,
    shuffleArray,
    Helmet
} from "@careers-wales/react-components";
import LandingController from "./routes/landing/controllers/LandingController";
import ResultsController from "./routes/results/controllers/ResultsController"
import QuizController from "./routes/stages/controllers/QuizController";
import {BreadcrumbsItem} from 'react-breadcrumbs-dynamic';
import useDecisionMakingApi from "./hooks/useDecisionMakingApi";

/**
 * App.
 * @param props
 * @returns {JSX.Element}
 * @constructor
 */
function App(props) {

    const {addPreLoginHandler, isAuthenticated} = useAuthentication();

    const [currentStage, setCurrentStage] = useState(0);

    // Answers API.
    const {getQuestions, submitQuiz, quizResults} = useDecisionMakingApi(process.env);

    // Hooks
    const navigate = useNavigate();

    // Translation.
    const {t, i18n} = useTranslation();

    // Response errors
    const {errors, setError} = useErrors();

    // Answers.
    const [decisions, setDecisions] = useState(null);

    // Loading
    const [isLoading, setIsLoading] = useState(false);

    // Store the quiz results.
    const [results, setResults] = useState(null);

    const [saved, setSaved] = useState(false);

    // Answers.
    const [initialAnswers, setInitialAnswers, initialAnswersRef] = useState([]);

    /**
     * Wipe all answers and return the user to the start.
     */
    function retakeQuiz() {
        // Remove any answers.
        setInitialAnswers([]);
        // Go back to stage 1.
        setCurrentStage(0);
        // Remove anything in session storage.
        SessionStorage.remove('decisionMakingAnswers');
        // Go to the homepage.
        navigate(route('quiz'));
    }

    /**
     * Submit the users answers to the API and retrieve their results.
     */
    function submit() {
        // Set up the payload structure.
        let payload = {
            "answers": []
        };

        initialAnswersRef.current.forEach((item,key) => {
            // Push choice 1.
            payload.answers.push({
                "type": decisions.questions[key].type,
                "dmqResponseId": item[0],
                "choice": decisions.questions[key].type === 'terminal' ? 1 : null
            });

            // Push choice 2.
            payload.answers.push({
                "type": decisions.questions[key].type,
                "dmqResponseId": item[1],
                "choice": decisions.questions[key].type === 'terminal' ? 2 : null
            });
        });

        // Send it!.
        submitQuiz(payload,
            i18n.language,
            (response) => {
                setResults(response.data);
                if (isAuthenticated()) {
                    setSaved(true);
                }
                navigate(route('results'));
            },
            (error) => {
                setError(error);
            }
        );

    }

    // Load the filters on first load.
    useEffect(() => {
        addPreLoginHandler(
            () => {
                if (initialAnswersRef.current.length > 0) {
                    SessionStorage.setNotNull('decisionMakingAnswers', JSON.stringify(initialAnswersRef.current));
                }
            }
        );
        loadAnswers();
    }, []);

    // Restore the users answers after logging in.
    useEffect(() => {
        // If there is data in the session then restore it.
        if ( SessionStorage.has('decisionMakingAnswers')) {
            restoreAnswers();
        }else {
            // Otherwise re-instate any saved results.
          restoreResults();
        }
    },[isAuthenticated()]);


    /**
     * Restore a users saved results.
     */
    function restoreResults() {
        setIsLoading(true);
        quizResults(
            i18n.language,
            (response) => {
                if (response.status === 200 ){
                    setIsLoading(false);
                    setResults(response.data);
                    navigate(route('results'));
                } else {
                    setIsLoading(false);
                }
            },
            (error) => {
                setIsLoading(false)
                setError(error);
            }
        );
    }

    /**
     * If the user is logged in then restore any answers saved prior to logging in.
     */
    function restoreAnswers() {
        // Is the user logged in.
        if (isAuthenticated()) {
            SessionStorage.getRemove('decisionMakingAnswers', (data) => {
                let savedAnswers;
                if (isJsonValid(data)) {
                    savedAnswers = JSON.parse(data);
                    // If the quiz is finished then submit the answers.
                    if (isQuizComplete(savedAnswers)) {
                       // console.log('Quiz completed so resubmitting',savedAnswers);
                        setInitialAnswers(savedAnswers);
                        submit();
                    } else {
                        // console.log('Quiz incomplete so restoring.',savedAnswers);
                        setInitialAnswers(savedAnswers);
                        // Navigate to last filled in question.
                        setCurrentStage(savedAnswers.length ? savedAnswers.length -1 : 0);
                    }
                } else {
                    // If the json was corrupted then remove it.
                    SessionStorage.remove('decisionMakingAnswers');
                    console.log('Invalid JSON');
                }
            }, () => {
                // @todo make the error callback optional.
            })
        }
    }

    /**
     * isQuizComplete
     * @param data
     */
    function isQuizComplete(data) {
        // a quiz is considered complete if the amount of answers is twice the number of questions.
        const totalAnswers = data.reduce((count, current) => count + current.length, 0);

        return totalAnswers === (decisions.questions.length * 2);
    }

    /**
     * loadAnswers
     */
    function loadAnswers() {
        getQuestions(
            i18n.language,
            (response) => {
                let data = response.data;

                data.questions.forEach((element,index) => {
                    data.questions[index].answers =   shuffleArray(data.questions[index].answers);
                });
               setDecisions(data);
            },
            (error) => {
               setError(error);
            }
        );
    }

    /**
     * getAnswerByStageId
     * @param stage
     * @param id
     */
    function getAnswerByStageId(stage, id) {
        return decisions.questions[stage].answers.find(obj => obj.dmqResponseId === id);
    }

    /**
     * Render return.
     */
    return (
        <>
            <Helmet>
                <title>{t('app.name')}</title>
                <meta name="description" content={t('app.description')}/>
            </Helmet>

            <BreadcrumbsItem to={route('landing')}>
                {t('view.landing.breadcrumb')}
            </BreadcrumbsItem>

            <Response hasLoaded={decisions !== null && !isLoading} errors={errors}>

                <Routes>
                    <Route
                        key={'landing'}
                        path={route('landing')}
                        element={
                            <LandingController
                                setCurrentStage={setCurrentStage}
                            />
                        }
                    />

                    <Route
                        key={'quiz'}
                        path={route('quiz')}
                        element={
                            <QuizController
                                answers={initialAnswers}
                                setAnswers={setInitialAnswers}
                                currentStage={currentStage}
                                setCurrentStage={setCurrentStage}
                                questions={decisions}
                                getAnswerByStageId={getAnswerByStageId}
                                submit={submit}
                                isQuizComplete={isQuizComplete}
                            />
                        }
                    />

                    <Route
                        key={'results'}
                        path={route('results')}
                        element={
                            <ResultsController
                                saved={saved}
                                results={results}
                                retakeQuiz={retakeQuiz}
                            />
                        }
                    />

                    <Route path="*" element={<Container><NotFound/></Container>}/>
                </Routes>
            </Response>
        </>
    );
}

export default App;
